import { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { apiFetch } from "../../../config/api";
import Loader from "../../../components/Loader";
import { getUser } from "../../../config/auth";
import FilterChip from "../../../components/FilterChip";
import { canValidateTasks } from "../../../config/rbac";
import Pagination, { usePagination } from "../../../components/Pagination";
import StatusPill from "../../../components/StatusPill";
import PriorityBadge from "../../../components/PriorityBadge";
import EmptyState from "../../../components/EmptyState";
import { formatDate } from "../../../config/utils";

const PAGE_SIZE = 10;

export default function PendingValidationPage() {
  const me = getUser();
  const role = me?.role || "Staff";
  const allowed = canValidateTasks(role);

  const [tasks, setTasks] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [page, setPage] = useState(1);
  const [deptFilter, setDeptFilter] = useState("");
  const [priorityFilter, setPriorityFilter] = useState("");

  const navigate = useNavigate();

  const filteredTasks = useMemo(() => {
    let r = tasks;
    if (deptFilter) r = r.filter((t) => String(t.assignedToUserDepartmentId) === String(deptFilter));
    if (priorityFilter) r = r.filter((t) => t.priority === priorityFilter);
    return r;
  }, [tasks, deptFilter, priorityFilter]);

  const hasFilter = deptFilter || priorityFilter;

  // Hooks must always be called before any early return
  const { totalPages, safePage, paginated } = usePagination(filteredTasks, PAGE_SIZE);
  const currentPage = safePage(page);
  const pagedTasks = paginated(currentPage);

  async function load() {
    setLoading(true);
    setError("");
    try {
      const res = await apiFetch("/api/tasks/visible?status=UnderValidation&limit=500");
      if (!res.ok) throw new Error(await res.text());
      const data = await res.json();
      const list = Array.isArray(data) ? data : (data.items || []);
      setTasks(list);
    } catch (e) {
      setError(e?.message || "Failed to load tasks");
      setTasks([]);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    apiFetch("/api/departments").then(r => r.json()).then(d => setDepartments(Array.isArray(d) ? d : [])).catch(() => {});
  }, []);

  useEffect(() => {
    if (allowed) load();
    else setLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allowed]);

  if (!allowed) {
    return (
      <div>
        <div style={{ marginBottom: 28 }}>
          <h2 style={{ marginBottom: 4 }}>Validations</h2>
          <p style={{ color: "var(--text-2)", fontSize: 14 }}>
            Manager/Head/CXO/Director/Admin only
          </p>
        </div>
        <div className="glass-card" style={{ padding: 24 }}>
          You don't have permission to view this page.
        </div>
      </div>
    );
  }

  return (
    <div>
      <div
        style={{
          marginBottom: 20,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-end",
        }}
      >
        <div>
          <h2 style={{ marginBottom: 4 }}>Validations</h2>
          <p style={{ color: "var(--text-2)", fontSize: 14, margin: 0 }}>
            Tasks marked as <b>Under Validation</b>
          </p>
        </div>
        <button
          className="btn-ghost"
          onClick={load}
          disabled={loading}
          style={{ padding: "10px 14px", borderRadius: 12 }}
        >
          Refresh
        </button>
      </div>

      {error && (
        <div style={{ marginBottom: 16, padding: "10px 14px", borderRadius: 8, background: "var(--danger-soft)", border: "1px solid var(--danger-border)", color: "var(--danger-text)", fontSize: 13 }}>{error}</div>
      )}

      {/* ── Filter Bar ── */}
      <div style={{
        display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap",
        marginBottom: 14, padding: "10px 14px",
        background: "var(--surface)", border: "1px solid var(--border)", borderRadius: 12,
      }}>
        <span style={{ fontSize: 11, fontWeight: 700, color: "var(--text-3)", textTransform: "uppercase", letterSpacing: "0.1em", marginRight: 2 }}>Filter</span>
        {departments.length > 0 && (
          <FilterChip value={deptFilter} onChange={(v) => { setDeptFilter(v); setPage(1); }} label="Department"
            options={departments.map((d) => ({ value: d.id, label: d.name }))} />
        )}
        <FilterChip value={priorityFilter} onChange={(v) => { setPriorityFilter(v); setPage(1); }} label="Priority"
          options={[{ value: "High", label: "🔴 High" }, { value: "Medium", label: "🟡 Medium" }, { value: "Low", label: "🟢 Low" }]} />
        {hasFilter && (
          <>
            <div style={{ width: 1, height: 18, background: "var(--border-strong)", margin: "0 4px" }} />
            <button onClick={() => { setDeptFilter(""); setPriorityFilter(""); setPage(1); }}
              style={{ padding: "5px 10px", borderRadius: 6, fontSize: 12, fontWeight: 600, background: "var(--danger-bg)", color: "var(--danger-text)", border: "1px solid #fecdd3", cursor: "pointer" }}>
              ✕ Clear
            </button>
            <span style={{ fontSize: 12, color: "var(--text-3)" }}>{filteredTasks.length} of {tasks.length} tasks</span>
          </>
        )}
      </div>

      <div className="glass-card" style={{ padding: 0 }}>
        <div className="tasks-table">
          <div className="tasks-row header" style={{ gridTemplateColumns: "2fr 1.2fr 1fr 0.8fr 1fr" }}>
            <div>Task</div>
            <div>Owner</div>
            <div>Due Date</div>
            <div>Priority</div>
            <div>Status</div>
          </div>

          {loading && <Loader.TableRows rows={8} />}

          {!loading && filteredTasks.length === 0 && (
            <EmptyState
              icon="✅"
              title={hasFilter ? "No tasks match your filters" : "All clear — nothing to review"}
              subtitle={hasFilter ? "Try adjusting or clearing your filters." : "Tasks submitted for validation will appear here."}
            />
          )}

          {!loading &&
            pagedTasks.map((t) => (
              <div
                key={t.id}
                className="tasks-row"
                style={{ gridTemplateColumns: "2fr 1.2fr 1fr 0.8fr 1fr" }}
                onClick={() => navigate(`/tasks/${t.id}`)}
              >
                <div className="task-title">{t.title}</div>
                <div style={{ color: "var(--text-2)" }}>
                  {t.assignedToUserName || "—"}
                </div>
                <div className="due-date">{formatDate(t.dueDate)}</div>
                <div>
                  <PriorityBadge priority={t.priority} />
                </div>
                <div>
                  <StatusPill status={t.status} />
                </div>
              </div>
            ))}
        </div>
        <Pagination
          page={currentPage}
          totalPages={totalPages}
          onPageChange={(p) => setPage(safePage(p))}
          total={filteredTasks.length}
          pageSize={PAGE_SIZE}
        />
      </div>
    </div>
  );
}

